<template>
  <div class="multimedia__container">
    <div :class="multimediaClass">
      <div class="multimedia__overlay" v-if="showOverlay">
        <div class="multimedia__overlay-text">{{ disabledTab }}</div>
      </div>
      <tabs @change-tab="changeTab">
        <tab
          v-for="(tab, index) in tabs"
          :class="{
            'multimedia__disabled-ws':
              disabledWhatsapp &&
              tab.title !== 'Template' &&
              tab.title !== $t('campaigns.title'),
          }"
          :key="index"
          :icon="tab.icon"
          :name="tab.title"
          :selected="tab.selected"
        >
          <component
            :is="tab.component"
            :selectedCampaign="selectedCampaign || campaignId"
            :selectedCampaignName="getCampaignName(selectedCampaign || campaignId)"
            @data-bank-picked="dataBankPicked"
            @multimedia-picked="multimediaPicked"
          />
        </tab>
      </tabs>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import {
  CHAT_MULTIMEDIA_TOGGLE,
} from '@/eventTypes';
import EventBus from '@/eventBus';

export default {
  components: {
    Tab: () => import('@/components/multimedia/Tab.vue'),
    Tabs: () => import('@/components/multimedia/Tabs.vue'),
    OptionsAttachment: () => import('@/components/multimedia/options/Attachment.vue'),
    OptionsDocuments: () => import('@/components/multimedia/options/Documents.vue'),
    OptionsImages: () => import('@/components/multimedia/options/Images.vue'),
    OptionsLocations: () => import('@/components/multimedia/options/Locations.vue'),
    OptionsTemplateText: () => import('@/components/multimedia/options/TemplateText.vue'),
    OptionsText: () => import('@/components/multimedia/options/Text.vue'),
    OptionsVideos: () => import('@/components/multimedia/options/Videos.vue'),
    OptionsCampaigns: () => import('@/components/multimedia/options/Campaigns.vue'),
  },
  data() {
    return {
      isOpened: false,
      options: [
        {
          icon: 'type-text-icon',
          title: this.$t('multimedia.texts'),
          selected: true,
          component: 'options-text',
        },
        {
          icon: 'type-image-icon',
          title: this.$t('multimedia.images'),
          selected: false,
          component: 'options-images',
        },
        {
          icon: 'type-document-icon',
          title: this.$t('multimedia.documents'),
          selected: false,
          component: 'options-documents',
        },
        {
          icon: 'type-video-icon',
          title: this.$t('multimedia.videos'),
          selected: false,
          component: 'options-videos',
        },
        {
          icon: 'type-location-icon',
          title: this.$t('multimedia.locations'),
          selected: false,
          component: 'options-locations',
        },
      ],
      selectedTab: this.$t('multimedia.texts'),
    };
  },
  computed: {
    ...mapState({
      selectedCampaign: (state) => state.currentCampaign,
    }),
    ...mapGetters([
      'activeConversation',
      'activeConversationHistory',
      'getConfig',
      'disabledWhatsapp',
      'getUserCampaigns',
    ]),
    multimediaClass() {
      return [
        'multimedia',
        {
          'is-opened': this.isOpened,
        },
      ];
    },
    tabs() {
      let options = [...this.options];

      if (this.isWhatsApp) {
        options = [
          ...options,
          {
            icon: 'type-template-icon',
            title: 'Template',
            selected: false,
            component: 'options-template-text',
          },
        ];
      }

      if (this.showAttachOption) {
        options = [
          ...options,
          {
            icon: 'type-attach-icon',
            title: this.$t('multimedia.custom'),
            selected: false,
            component: 'options-attachment',
          },
        ];
      }

      return options;
    },
    conversation() {
      return this.activeConversation ? this.activeConversation.conversation ?? {} : {};
    },
    conversationHistory() {
      return this.activeConversationHistory
        ? this.activeConversationHistory.conversation ?? {} : {};
    },
    campaign() {
      return (this.conversation.campaign || this.conversationHistory.campaign) ?? {};
    },
    channel() {
      return (this.conversation.channel || this.conversationHistory.channel) ?? {};
    },
    campaignId() {
      return this.campaign.id ?? null;
    },
    channelId() {
      return this.channel.id ?? null;
    },
    isWhatsApp() {
      return this.channel.channelType && this.channel.channelType.key === 'whatsapp';
    },
    configuration() {
      return this.getConfig(this.campaignId, this.channelId);
    },
    customAudio() {
      return this.configuration.customAudio ?? false;
    },
    customDocuments() {
      return this.configuration.customDocuments ?? false;
    },
    customImages() {
      return this.configuration.customImages ?? false;
    },
    showAttachOption() {
      return this.customAudio || this.customDocuments || this.customImages;
    },
    disabledTab() {
      return this.$t('multimedia.disabled', { tab: this.selectedTab });
    },
    optionIndex() {
      return this.options.findIndex((option) => option.title === this.selectedTab);
    },
    optionCampaignsIndex() {
      return this.tabs.findIndex((option) => option.title === this.$t('campaigns.title'));
    },
    showOverlay() {
      return (this.disabledWhatsapp && this.optionIndex !== -1);
    },
  },
  created() {
    EventBus.$on(CHAT_MULTIMEDIA_TOGGLE, (opened) => {
      this.toggle(opened);
    });
  },
  destroyed() {
    EventBus.$off(CHAT_MULTIMEDIA_TOGGLE);
  },
  methods: {
    dataBankPicked(data) {
      const msg = {
        text: null,
        type: data.type,
        link: data[data.type] ? data[data.type].url : null,
        location: data.location ?? null,
        filename: data[data.type] ? data[data.type].filename : 'File',
      };

      this.$emit('send-message', { message: msg, campaign: data.campaign || this.campaignId });
      EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, false);
    },
    multimediaPicked(payload) {
      const msg = {
        ...payload,
        text: null,
      };

      this.$emit('send-message', {
        message: msg,
        campaign: this.selectedCampaign || this.campaignId,
      });
      EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, false);
    },
    toggle(opened) {
      this.isOpened = opened;
    },
    changeTab(tab) {
      if (this.selectedCampaign) {
        this.selectedTab = tab.name;
      }
    },
    getCampaignName(id) {
      return this.getUserCampaigns.find((campaign) => campaign.id === id)?.name || null;
    },
  },
};
</script>

<style scoped lang="scss">
@import "~styles/components/chat-multimedia.scss";
</style>
